import { useState, useCallback, useEffect } from 'react';
import { taskService } from '../services/taskService';
import {
  TaskData,
  TaskStatus,
  TaskPriority,
  TaskCategory,
  CreateTaskRequest,
  UpdateTaskRequest,
  GetTasksQuery,
} from '../types/task';

export interface TaskFilters {
  status?:     TaskStatus;
  priority?:   TaskPriority;
  category?:   TaskCategory;
  sort_by?:    GetTasksQuery['sort_by'];
  sort_order?: 'asc' | 'desc';
}

interface Pagination {
  page:        number;
  limit:       number;
  total:       number;
  totalPages:  number;
  hasNext:     boolean;
  hasPrevious: boolean;
}

export function useTaskManagement(calendarYear?: number, calendarMonth?: number) {
  const [tasks, setTasks]               = useState<TaskData[]>([]);
  const [isLoading, setIsLoading]       = useState(false);
  const [error, setError]               = useState<string | null>(null);
  const [pagination, setPagination]     = useState<Pagination | null>(null);
  const [filters, setFilters]           = useState<TaskFilters>({});
  const [page, setPage]                 = useState(1);

  const [celebrateTaskId, setCelebrateTaskId] = useState<number | null>(null);

  const fetchTasks = useCallback(async (currentFilters: TaskFilters = filters, currentPage = page) => {
    setIsLoading(true);
    setError(null);
    try {
      const query: GetTasksQuery = {
        ...currentFilters,
        sort_by:    currentFilters.sort_by ?? 'due_date',
        sort_order: currentFilters.sort_order ?? 'asc',
        page:  currentPage,
        limit: 100,
        ...(calendarYear !== undefined && calendarMonth !== undefined
          ? { year: calendarYear, month: calendarMonth }
          : {}),
      };
      const res = await taskService.getAll(query);
      setTasks(res.data);
      if (res.meta?.pagination) {
        setPagination(res.meta.pagination);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? 'タスクの取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, [filters, page, calendarYear, calendarMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchTasks(filters, page);
  }, [filters, page, calendarYear, calendarMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  const createTask = useCallback(async (data: CreateTaskRequest): Promise<void> => {
    const task = await taskService.create(data);
    if (calendarYear !== undefined && calendarMonth !== undefined && task.due_date) {
      const [taskYear, taskMonth] = task.due_date.split('-').map(Number);
      if (taskYear === calendarYear && taskMonth === calendarMonth) {
        setTasks((prev) => [task, ...prev]);
      }
      // 別月のタスクは次のリフェッチ時に正しく反映される
    } else {
      setTasks((prev) => [task, ...prev]);
    }
  }, [calendarYear, calendarMonth]);

  const updateTask = useCallback(async (id: number, data: UpdateTaskRequest): Promise<void> => {
    const updated = await taskService.update(id, data);

    if (data.status === TaskStatus.COMPLETED) {
      setCelebrateTaskId(id);
      setTimeout(() => setCelebrateTaskId(null), 2000);
    }

    setTasks((prev) => prev.map((t) => (t.id === id ? updated : t)));
  }, []);

  const deleteTask = useCallback(async (id: number): Promise<void> => {
    await taskService.delete(id);
    setTasks((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const applyFilters = useCallback((newFilters: TaskFilters) => {
    setFilters(newFilters);
    setPage(1);
  }, []);

  const PRIORITY_RANK: Record<string, number> = {
    [TaskPriority.HIGH]:   0,
    [TaskPriority.MEDIUM]: 1,
    [TaskPriority.LOW]:    2,
  };

  // 完了済みを末尾に固定。「優先度」並び順の場合は同一優先度内を締切日昇順でさらに整列。
  const sortedTasks = [...tasks].sort((a, b) => {
    const aCompleted = a.status === TaskStatus.COMPLETED ? 1 : 0;
    const bCompleted = b.status === TaskStatus.COMPLETED ? 1 : 0;
    if (aCompleted !== bCompleted) return aCompleted - bCompleted;

    if (filters.sort_by === 'priority') {
      const priorityDiff = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
      if (priorityDiff !== 0) return priorityDiff;
      return a.due_date.localeCompare(b.due_date);
    }

    return 0;
  });

  return {
    tasks: sortedTasks,
    isLoading,
    error,
    pagination,
    filters,
    page,
    celebrateTaskId,
    fetchTasks,
    createTask,
    updateTask,
    deleteTask,
    applyFilters,
    setPage,
  };
}
